/**
 * Business-capability access model.
 *
 * The platform template only ships coarse platform roles (admin / user /
 * connector); the real business roles live in each user's `title`. This maps a
 * signed-in user to the set of menu slugs they may reach, so the nav renders
 * only role-appropriate links (no dead/forbidden entries) and route guards can
 * check capability rather than the raw platform role.
 *
 * Internal staff operate the admin-gated workspaces, so they carry platform role
 * `admin`; their `title` narrows what they see. `admin` with no specific title
 * is the SYSTEM ADMINISTRATOR — full per-company administration, but NOT the
 * group-wide console. That group console belongs to the Group Admin / Super
 * Admin (`super_admin` role). Connectors get the connector workspace only.
 */

const CONNECTOR_SLUGS = ['connector-dashboard', 'connector-referrals', 'connector-commissions', 'new-referral', 'connector-profile', 'connector-onboarding'];
// Connector-only workspaces — excluded even from the System Admin superuser nav,
// since they render the signed-in connector's own data (not an admin surface).
const CONNECTOR_ONLY = ['connector-dashboard', 'connector-referrals', 'connector-commissions', 'new-referral', 'connector-profile', 'connector-onboarding'];
// The group-wide console is Group Admin (super_admin) territory only — the
// per-company System Admin superuser does NOT get it.
const SUPERADMIN_ONLY = ['superadmin'];
// Payout exports carry FULL bank account numbers, which only the Finance title
// may see (mirrors the backend's export gate) — excluded from the '*' superuser.
const FINANCE_ONLY = ['export-handoff-management'];

/**
 * Only the Finance title may see unmasked bank account numbers, and so only it
 * may take the payout FILE (utils/maskAccount.js on the server is the rule this
 * mirrors). Anyone else holding Generate Export Sheets works the queue with
 * numbers masked to the last 4 and leaves the download to Finance (QA 8283).
 */
export const canViewFullBank = (user) => user?.title === 'Finance User';

/**
 * Every governance title the user holds, primary first. One user may hold
 * Relationship Manager AND Branch Operations (QA 8562); the server sends the
 * second as `secondary_title`. Everything title-based below reads through this.
 */
export function userTitles(user) {
  const out = [];
  if (user?.title) out.push(user.title);
  const second = user?.secondary_title ?? user?.secondaryTitle;
  if (second && second !== user?.title) out.push(second);
  return out;
}

// Alternate URL paths (router aliases + detail routes) -> the canonical menu
// slug that carries the capability. Keeps TITLE_SLUGS keyed on nav slugs only.
const SLUG_ALIASES = {
  'connector-accreditation': 'connector-accreditation-management',
  'commission-configuration': 'commission-configuration-approvals',
  'commission-processing': 'commission-processing-queue',
  'export-handoff': 'export-handoff-management',
  'referral-details': 'referral-queue',
  'new-referral-wizard': 'new-referral',
};

// Business role (title) -> reachable menu slugs. '*' = everything (Group Admin).
const TITLE_SLUGS = {
  'Group Admin': ['*'],
  // Provisionable System Administrator. A seeded System Admin carries NO title
  // and reaches '*' by falling through to the role check below; one created
  // through Provision New Employee has to carry a title (the form stores what
  // it is given), so map it explicitly rather than leaning on that fall-through
  // — both routes must land on the same capability set.
  'System Administrator': ['*'],
  'Loans Officer': ['commission-processing-queue'],
  // Compliance reviews accreditations AND audit chains (review_audit_chains →
  // the Audit Logs / Security Monitoring page). QA 7822.
  'Compliance / Accreditation Officer': ['connector-accreditation-management', 'audit-logs-security-monitoring'],
  'Account Management': ['referral-queue'],
  'Account Management (Intake)': ['referral-queue'],
  'Branch Operations': ['referral-queue'],
  'Relationship Manager': ['referral-queue'],
  'Relationship Manager (RM)': ['referral-queue'],
  // Finance generates export sheets AND processes commissions (process_commissions
  // → the Commission Processing queue). QA 7822.
  'Finance User': ['export-handoff-management', 'commission-processing-queue'],
};

/**
 * Capability granted in the Role Permissions matrix -> the module it unlocks
 * (QA 7822).
 *
 * The matrix used to be write-only: granting a capability persisted a row and
 * nothing ever read it, so navigation never changed and the feature was inert.
 * These links were already documented in the comments below as prose; this
 * codifies them so a grant actually reaches the person.
 *
 * For a title the matrix GOVERNS (`capabilityRole` set), this map is the whole
 * access set: granting adds a module and un-ticking removes it (QA 8244 — the
 * additive-only model meant a Finance User kept the Commission Processing queue
 * after Process Commissions was un-ticked, so the configured permissions and
 * the delivered ones disagreed).
 *
 * That is safe because every catalog role's DEFAULT grants map exactly onto the
 * TITLE_SLUGS baseline for its title, so an untouched matrix yields precisely
 * the previous access — the server falls back to those defaults per capability
 * whenever no row has been stored (see grantedCapabilitiesFor). Titles outside
 * the catalog (System Administrator, Loans Officer, Group Admin) carry a null
 * capabilityRole and keep their baseline untouched.
 */
const CAPABILITY_SLUGS = {
  review_accreditations: ['connector-accreditation-management'],
  review_audit_chains: ['audit-logs-security-monitoring'],
  update_loan_milestones: ['referral-queue'],
  route_referrals: ['referral-queue'],
  configure_commission_rates: ['commission-configuration-approvals'],
  generate_export_sheets: ['export-handoff-management'],
  process_commissions: ['commission-processing-queue'],
  provision_users: ['user-administration-security'],
  modify_permissions: ['role-permission-management'],
  release_lockouts: ['user-administration-security'],
};

/** Extra slugs unlocked by the capabilities granted to this user's role. */
function capabilitySlugs(user) {
  const caps = Array.isArray(user?.capabilities) ? user.capabilities : [];
  return [...new Set(caps.flatMap((c) => CAPABILITY_SLUGS[c] || []))];
}

/** The slugs a user may reach; ['*'] means all. */
export function allowedSlugs(user) {
  if (!user) return [];
  // Group Super Admin + read-only viewers live entirely in the /superadmin console.
  if (user.role === 'super_admin' || user.role === 'group_viewer') return ['superadmin'];
  // Creating a referral is the one connector workspace that depends on more
  // than being a connector: accreditation has to be approved first (QA 8180).
  // Gating it here rather than on the button means the nav link, the mobile FAB
  // and the /new-referral route are all covered by the one rule — the wizard
  // was reachable by URL even while the dashboard button was disabled.
  //
  // Absent status = not yet loaded/unknown, which is treated as unapproved.
  // The backend refuses the create either way (referral.service.js), so this is
  // presentation catching up with a rule that was already enforced.
  if (user.role === 'connector') {
    if (user.accreditationStatus === 'approved') return CONNECTOR_SLUGS;
    return CONNECTOR_SLUGS.filter((s) => s !== 'new-referral');
  }
  // A title the Role Permissions matrix governs is defined BY its capabilities:
  // what the matrix says is what the user gets, in both directions (QA 8244).
  // Sessions issued before capabilityRole existed have no such marker and fall
  // through to the additive path below, so nobody loses access mid-session.
  if (user.capabilityRole) return capabilitySlugs(user);
  const byTitle = userTitles(user).flatMap((t) => TITLE_SLUGS[t] || []);
  if (byTitle.length) return [...new Set([...byTitle, ...capabilitySlugs(user)])];
  // Platform admin with no mapped business title = System Administrator (full
  // per-company administration). The group console is NOT included here.
  if (user.role === 'admin') return ['*'];
  return [];
}

export function canAccessSlug(user, slug) {
  const resolved = SLUG_ALIASES[slug] || slug;
  const slugs = allowedSlugs(user);
  if (slugs.includes(resolved)) return true;
  // System Admin superuser sees everything EXCEPT connector-only workspaces,
  // the group-wide console (Group Admin / super_admin only), and payout exports
  // (Finance only — full bank numbers).
  return (
    slugs.includes('*') &&
    !CONNECTOR_ONLY.includes(resolved) &&
    !SUPERADMIN_ONLY.includes(resolved) &&
    !FINANCE_ONLY.includes(resolved)
  );
}

/**
 * Which action sections a user may operate on the Referral Details page.
 * AM/BO/RM all reach the page (shared 'referral-queue' capability) but each
 * stage's actions belong to one business role; superusers ('*') get all three.
 */
export function referralActionAccess(user) {
  const slugs = allowedSlugs(user);
  const all = slugs.includes('*');
  const titles = userTitles(user);
  const holds = (test) => titles.some(test);
  return {
    am: all || holds((t) => t.startsWith('Account Management')),
    branch: all || holds((t) => t === 'Branch Operations'),
    rm: all || holds((t) => t.startsWith('Relationship Manager')),
  };
}

/** Filter a menuData.items tree down to what the user can actually open. */
export function filterNav(items, user) {
  const visible = (slug) => canAccessSlug(user, slug);
  const keep = (node) => {
    if (node.children) {
      const kids = node.children.filter((c) => visible(c.slug));
      return kids.length ? { ...node, children: kids } : null;
    }
    return visible(node.slug) ? node : null;
  };
  return items.map(keep).filter(Boolean);
}

/**
 * Where a user should land after signing in — their home workspace, not the
 * generic hub. Connectors go to their dashboard; internal staff go to the first
 * workspace they can actually reach (Group Admin → admin dashboard). Falls back
 * to '/' only if the user has no reachable workspace.
 */
export function landingFor(user) {
  if (!user) return '/';
  if (user.role === 'super_admin' || user.role === 'group_viewer') return '/superadmin';
  if (user.role === 'connector') return '/connector-dashboard';
  const slugs = allowedSlugs(user);
  if (slugs.includes('*')) return '/admin-dashboard';
  if (slugs.length) return `/${slugs[0]}`;
  return '/';
}

/**
 * Where to send a user immediately after they sign in.
 *
 * ProtectedRoute/RoleRoute bounce anonymous visitors to /login carrying the
 * route they were denied (`state.from`). That state belongs to the browser
 * history entry, not to a session, so it outlives the sign-out that created it:
 * whoever signs in next on that screen would inherit the previous user's last
 * page (QA 7878). Honor it only when the SAME user comes back — anyone else
 * gets their own landing page.
 *
 * The capability check stays on top of that: a user returning to a route they
 * have since lost access to still lands somewhere they can actually be.
 */
export function postLoginDestination(user, { redirectOrigin, bouncedUserId } = {}) {
  // A redirectOrigin is only replayed for a COLD deep link — i.e. no session
  // ended in this tab (no stamp). If a stamp exists, a session actually ended
  // here (explicit logout OR idle timeout), and post-login must always go to the
  // role default, never back to the page the user left (QA 7913). This still
  // covers QA 7878 (a different user also gets their own landing) since any
  // stamp — same user or not — suppresses replay. Anonymous deep-links never
  // call clearSession, so no stamp is written and the feature is preserved.
  const mayReplay = bouncedUserId == null;
  if (redirectOrigin && mayReplay) {
    const slug = redirectOrigin.replace(/^\//, '').split('/')[0];
    if (canAccessSlug(user, slug)) return redirectOrigin;
  }
  return landingFor(user);
}

export default { allowedSlugs, canAccessSlug, filterNav, landingFor, postLoginDestination };
